import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, CardinalApiState, guard } from '../services/auth.api'
import { DecryptedService } from '@icure/cardinal-sdk'
import { Version, VersionedData } from '@icure/form/generic'
import { Form } from '@icure/form/components/model'
import { getAllFields, getFields, getOptions } from '../../helpers/fields'
import { resolveFormTemplate } from './formTemplateUtils'
import { getPatientDataFormated } from '../../helpers/patientDataManipulations'
import { getServiceForLabel, svcMeasure, svcText } from '../../helpers/serviceDataExtraction'
import { loadChildrenRecursively } from './formApi'
import { loadFromIterator } from './utils'
import { ContactFilters, FormFilters } from '@icure/cardinal-sdk/filters.mjs'

export type ExportMode = 'admin' | 'formData'
export type FormScope = 'all' | 'latest'

export interface ExportResult {
  rows: Record<string, string>[]
  columns: { id: string; label: string }[]
}

export const exportApiRtk = createApi({
  reducerPath: 'exportApi',
  tagTypes: ['Export'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    exportData: builder.query<
      ExportResult,
      {
        mode: ExportMode
        patients: DecryptedPatient[]
        formScope: FormScope
      }
    >({
      async queryFn({ mode, patients, formScope }, { getState }) {
        const sdk = await cardinalApi(getState)

        const {
          cardinalApi: { user },
        } = getState() as { cardinalApi: CardinalApiState }

        return guard(
          [sdk?.contact, sdk?.form, sdk?.healthcareParty, user?.healthcarePartyId],
          async ([contactApi, formApi, healthcarePartyApi, hcPartyId]): Promise<ExportResult> => {
            const hcpOrParentId = (await healthcarePartyApi.getHealthcareParty(hcPartyId))?.parentId ?? hcPartyId

            if (mode === 'admin') {
              const columns = [
                { id: 'firstName', label: 'First Name' },
                { id: 'lastName', label: 'Last Name' },
                { id: 'emailAddress', label: 'Email' },
                { id: 'userDateOfBirthOneString', label: 'Date of Birth' },
                { id: 'birthSex', label: 'Sex' },
                { id: 'language', label: 'Language' },
                { id: 'street', label: 'Street' },
                { id: 'postalCode', label: 'Postal Code' },
                { id: 'city', label: 'City' },
                { id: 'tags', label: 'Tags' },
              ]

              const rows = patients.map((patient) => {
                const data = getPatientDataFormated(patient)
                return {
                  'First Name': data.firstName ?? '',
                  'Last Name': data.lastName ?? '',
                  Email: data.emailAddress ?? '',
                  'Date of Birth': data.userDateOfBirthOneString ?? '',
                  Sex: data.birthSex ?? '',
                  Language: data.language ?? '',
                  Street: [data.street, data.houseNumber].filter(Boolean).join(' '),
                  'Postal Code': data.postalCode ?? '',
                  City: data.city ?? '',
                  Tags:
                    data.tags
                      ?.map((t) => t.code)
                      .filter(Boolean)
                      .join(', ') ?? '',
                }
              })

              return { rows, columns }
            }

            // Form data mode: load all patient forms once and index by patient
            const formsByPatient = new Map(
              await Promise.all(
                patients.map(async (patient) => {
                  const patientForms = await loadFromIterator(
                    await formApi.filterFormsBySorted(FormFilters.byPatientsOpeningDateForDataOwner(hcpOrParentId, [patient])),
                    1000,
                  )
                  return [patient.id, patientForms] as const
                }),
              ),
            )

            const allPatientForms = [...formsByPatient.values()].flat()
            const usedTemplateIds = [...new Set(allPatientForms.map((f) => f.formTemplateId).filter((id): id is string => !!id && id !== 'Preventi.yaml'))]

            const forms = await Promise.all(usedTemplateIds.map((id) => resolveFormTemplate(formApi, id).catch(() => null)))
            if (!forms.some(Boolean)) {
              // No specific templates found, fall back to latest
              const latest = await resolveFormTemplate(formApi, undefined).catch(() => null)
              if (latest) forms.push(latest)
            }
            const validForms = forms.filter((f): f is Form => f !== null)

            const seenFields = new Set<string>()
            const allFields = validForms.flatMap((form) =>
              getAllFields(form).filter((f) => {
                if (!f.field || seenFields.has(f.field)) return false
                seenFields.add(f.field)
                return true
              }),
            )
            const fieldColumns = allFields.map((f) => ({
              id: f.field!,
              label: `${f.field} (${f.shortLabel ?? f.field})`,
            }))

            const labelToText = (label: string): string | undefined => {
              for (const form of validForms) {
                const res = getFields(form, label)
                  .map((f) => f.shortLabel)
                  .join(', ')
                if (res.length) return res
              }
              return undefined
            }

            const optionToText = (label: string): string | undefined => {
              for (const form of validForms) {
                const res = getOptions(form, label)
                  .map((f) => f[1])
                  .join(', ')
                if (res.length) return res
              }
              return undefined
            }

            const baseColumns = [
              { id: 'firstName', label: 'First Name' },
              { id: 'lastName', label: 'Last Name' },
              { id: 'emailAddress', label: 'Email' },
              { id: 'userDateOfBirthOneString', label: 'Date of Birth' },
              { id: 'birthSex', label: 'Sex' },
              { id: 'language', label: 'Language' },
              { id: 'formDate', label: 'Form Date' },
            ]

            const columns = [...baseColumns, ...fieldColumns]
            const rows: Record<string, string>[] = []

            for (const patient of patients) {
              const data = getPatientDataFormated(patient)
              const baseRow: Record<string, string> = {
                'First Name': data.firstName ?? '',
                'Last Name': data.lastName ?? '',
                Email: data.emailAddress ?? '',
                'Date of Birth': data.userDateOfBirthOneString ?? '',
                Sex: data.birthSex ?? '',
                Language: data.language ?? '',
              }

              const patientForms = formsByPatient.get(patient.id) ?? []
              const parentForms = patientForms.filter((f) => !f.parent)

              if (parentForms.length === 0) {
                rows.push({ ...baseRow, 'Form Date': '' })
                continue
              }

              const formsToProcess =
                formScope === 'latest' ? [parentForms.reduce((latest, f) => ((f.openingDate ?? 0) > (latest.openingDate ?? 0) ? f : latest), parentForms[0])] : parentForms

              for (const parentForm of formsToProcess) {
                const formId = parentForm.id
                const formDate = parentForm.openingDate ? parentForm.openingDate.toString().replace(/(\d{4})(\d{2})(\d{2}).*/, '$3.$2.$1') : ''

                const childForms = await loadChildrenRecursively(formApi, hcpOrParentId, formId)
                const formIds = childForms.map((f) => f.id)
                const contactsHistory = await loadFromIterator(await contactApi.filterContactsBy(ContactFilters.byFormIdsForDataOwner(hcpOrParentId, [formId, ...formIds])), 10000)

                const indexedServices: {
                  [id: string]: Version<DecryptedService>[]
                } = contactsHistory.reduce((acc, ctc) => {
                  return (
                    ctc.services
                      ?.filter((s) => ctc.subContacts?.some((sc) => sc.formId === formId && sc.services?.some((sss) => sss.serviceId === s.id)))
                      ?.reduce(
                        (acc, s) =>
                          s.id
                            ? {
                                ...acc,
                                [s.id]: (acc[s.id] ?? (acc[s.id] = []))
                                  .concat({
                                    revision: ctc.rev ?? null,
                                    modified: s.modified ?? ctc.modified ?? s.created ?? ctc.created,
                                    value: s,
                                  })
                                  .sort((a, b) => -((a.modified ?? 0) < (b.modified ?? 0) ? -1 : (a.modified ?? 0) > (b.modified ?? 0) ? 1 : 0)),
                              }
                            : acc,
                        acc,
                      ) ?? acc
                  )
                }, {} as VersionedData<DecryptedService>)

                const language = patient.languages?.[0] ?? 'fr'
                const row: Record<string, string> = {
                  ...baseRow,
                  'Form Date': formDate,
                }

                for (const field of fieldColumns) {
                  const service = getServiceForLabel(indexedServices, field.id)
                  if (service) {
                    const measureResult = svcMeasure(language, service)
                    const textResult = svcText(language, service, labelToText, optionToText)
                    row[field.label] = measureResult !== '-' ? measureResult : textResult
                  } else {
                    row[field.label] = ''
                  }
                }

                rows.push(row)
              }
            }

            return { rows, columns }
          },
          { rows: [], columns: [] },
        )
      },
    }),
  }),
})

export const { useLazyExportDataQuery } = exportApiRtk
